import "./style.css";
import {
  coastChallenges,
  coastObjectives,
  coastQuest,
  finaleChallenges,
  finaleObjectives,
  finaleQuest,
  foundationQuest,
  healthChallenges,
  healthObjectives,
  healthQuest,
  muscleChallenges,
  muscleObjectives,
  muscleQuest,
  safariChallenges,
  safariObjectives,
  safariQuest,
} from "./domain/content";
import { explorers, isExplorerId } from "./domain/explorers";
import { chapterVocabulary } from "./domain/finale";
import { serializeLocalProgressExport } from "./domain/export";
import { completedCount, recordChallengeCompletion } from "./domain/progress";
import {
  GAME_VERSION,
  type ChallengeDefinition,
  type GameRegion,
  type PlayerPosition,
  type SaveGame,
} from "./domain/schema";
import { loadSave, resetSave, storeSave } from "./domain/storage";
import {
  EXPLORER_VISUAL_SCALE,
  SanctuaryGame,
  type WorldInteraction,
} from "./game/SanctuaryGame";
import { CoastChallengeController } from "./ui/CoastChallengeController";
import { FinaleChallengeController } from "./ui/FinaleChallengeController";
import { HealthChallengeController } from "./ui/HealthChallengeController";
import { MuscleChallengeController } from "./ui/MuscleChallengeController";
import { SkeletonChallengeController } from "./ui/SkeletonChallengeController";
import { TouchControls } from "./ui/TouchControls";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("App root not found");

app.innerHTML = `
  <main class="game-shell">
    <canvas id="game-canvas" tabindex="0" aria-label="Lily's Adventure 3D world"></canvas>

    <header class="hud hud--top" aria-label="Adventure status">
      <div class="brand">
        <span id="region-name" class="brand__eyebrow">Wildlife Sanctuary</span>
        <strong>Lily's Adventure</strong>
      </div>
      <div class="hud-actions">
        <button id="zoom-reset-button" class="round-button touch-only" type="button" aria-label="Reset interface zoom">1×</button>
        <button id="map-button" class="round-button" type="button" aria-label="Open trail map">⌖</button>
        <button id="journal-button" class="round-button round-button--journal" type="button" aria-label="Open field journal">⌘</button>
        <button id="pause-button" class="round-button" type="button" aria-label="Pause game">Ⅱ</button>
      </div>
    </header>

    <section class="quest-card" aria-labelledby="quest-title">
      <span class="quest-card__label">Current quest</span>
      <strong id="quest-title"></strong>
      <span id="quest-step"></span>
      <div id="trail-progress" class="trail-progress" aria-label="Trail clues restored"></div>
    </section>

    <button id="interaction-prompt" class="interaction-prompt" type="button" aria-keyshortcuts="E" hidden>
      <kbd class="keyboard-instruction">E</kbd><span id="interaction-label">Interact</span>
    </button>

    <section id="touch-controls" class="touch-controls" aria-label="Touch exploration controls" hidden>
      <div class="touch-move" role="group" aria-label="Move explorer">
        <button class="touch-button touch-move__forward" type="button" data-touch-direction="forward" aria-label="Move forward">▲</button>
        <button class="touch-button touch-move__left" type="button" data-touch-direction="left" aria-label="Move left">◀</button>
        <button class="touch-button touch-move__back" type="button" data-touch-direction="back" aria-label="Move backward">▼</button>
        <button class="touch-button touch-move__right" type="button" data-touch-direction="right" aria-label="Move right">▶</button>
      </div>
      <div class="touch-actions">
        <button class="touch-action touch-action--jump" type="button" data-touch-jump>Jump</button>
        <div class="touch-look" role="group" aria-label="Drag here to look around" data-touch-look><span>Drag to look</span><strong aria-hidden="true">✣</strong></div>
      </div>
    </section>

    <aside class="orientation-notice" aria-label="iPad orientation tip">
      <strong>Turn iPad sideways for easier exploring.</strong>
      <span>Learning panels still work in portrait.</span>
    </aside>

    <section id="welcome-panel" class="modal-card welcome-card" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
      <span class="pill">A nature and science adventure</span>
      <h1 id="welcome-title">Welcome back to the sanctuary!</h1>
      <p>Choose your explorer, then travel with your chameleon through four friendly science regions.</p>
      <section class="explorer-picker" aria-labelledby="welcome-explorer-title">
        <h2 id="welcome-explorer-title">Choose your explorer</h2>
        <p id="welcome-explorer-help">Every explorer moves, learns, and earns the same rewards. Your choice stays only in this browser.</p>
        <div id="welcome-explorer-options" class="explorer-grid" aria-describedby="welcome-explorer-help"></div>
        <output id="welcome-explorer-status" class="picker-status" aria-live="polite"></output>
      </section>
      <div class="control-grid desktop-instructions" aria-label="Keyboard and mouse controls">
        <span><kbd>W A S D</kbd> Move</span>
        <span><kbd>Space</kbd> Jump</span>
        <span><kbd>Drag</kbd> Look around</span>
        <span><kbd>E</kbd> Use named action</span>
        <span><kbd>Esc</kbd> Pause</span>
      </div>
      <div class="control-grid touch-instructions" aria-label="Touch controls">
        <span><kbd>▲ ◀ ▼ ▶</kbd> Move</span>
        <span><kbd>Drag</kbd> Look around</span>
        <span><kbd>Jump</kbd> Jump</span>
        <span><kbd>Named action</kbd> Tap nearby character or station</span>
        <span><kbd>⌖</kbd> Trail Map avoids tricky movement</span>
      </div>
      <button id="start-button" class="primary-button" type="button">Continue adventure</button>
    </section>

    <section id="dialogue-panel" class="modal-card dialogue-card" role="dialog" aria-modal="true" aria-labelledby="speaker" hidden>
      <div class="dialogue-character">
        <span id="speaker-portrait" class="speaker-portrait" aria-hidden="true">🦎</span>
        <div><span class="dialogue-kicker">Your wildlife guide</span><h2 id="speaker"></h2></div>
      </div>
      <p id="dialogue-text"></p>
      <button id="dialogue-close" class="primary-button" type="button">Let's go!</button>
    </section>

    <section id="portal-panel" class="modal-card dialogue-card" role="dialog" aria-modal="true" aria-labelledby="portal-title" hidden>
      <span class="pill">Trail gateway</span>
      <h2 id="portal-title">Travel to another world?</h2>
      <p id="portal-description"></p>
      <div class="portal-actions">
        <button id="portal-go" class="primary-button" type="button">Go through gateway</button>
        <button id="portal-not-now" class="secondary-button" type="button">Not now</button>
      </div>
    </section>

    <section id="challenge-panel" class="modal-card challenge-card" role="dialog" aria-modal="true" aria-labelledby="challenge-title" hidden></section>

    <section id="map-panel" class="modal-card journal-card" role="dialog" aria-modal="true" aria-labelledby="map-title" hidden>
      <div class="challenge-heading">
        <div><span class="pill">No precision movement needed</span><h2 id="map-title">Sanctuary Trail Map</h2></div>
        <button class="icon-button" id="map-close" type="button" aria-label="Close trail map">×</button>
      </div>
      <div id="map-content"></div>
    </section>

    <section id="journal-panel" class="modal-card journal-card" role="dialog" aria-modal="true" aria-labelledby="journal-title" hidden>
      <div class="challenge-heading">
        <div><span class="pill">Private and saved only here</span><h2 id="journal-title">Explorer's Field Journal</h2></div>
        <button class="icon-button" id="journal-close" type="button" aria-label="Close field journal">×</button>
      </div>
      <div id="journal-content"></div>
    </section>

    <section id="reward-panel" class="modal-card reward-card" role="dialog" aria-modal="true" aria-labelledby="reward-title" hidden>
      <span id="reward-icon" class="reward-badge" aria-hidden="true">🐇</span>
      <span id="reward-region" class="pill">Region restored</span>
      <h2 id="reward-title">Adventure reward!</h2>
      <p id="reward-text"></p>
      <button id="reward-close" class="primary-button" type="button">Return to the trail</button>
    </section>

    <section id="pause-panel" class="modal-card pause-card" role="dialog" aria-modal="true" aria-labelledby="pause-title" hidden>
      <span class="pill">Take your time</span>
      <h2 id="pause-title">Adventure paused</h2>
      <button id="resume-button" class="primary-button" type="button">Keep exploring</button>
      <button id="restart-button" class="secondary-button" type="button">Return to this region's start</button>
      <button id="motion-button" class="secondary-button" type="button"></button>
      <button id="text-button" class="secondary-button" type="button"></button>
      <button id="voice-button" class="secondary-button" type="button"></button>
      <button id="export-button" class="secondary-button" type="button">Adult: export local progress…</button>
      <button id="reset-button" class="text-button" type="button">Adult: reset saved adventure…</button>
      <p class="diagnostics">Adventure ${GAME_VERSION} · <span id="fps-reading">measuring smoothness…</span></p>
    </section>

    <output id="announcement" class="announcement" aria-live="polite"></output>
    <output id="game-state" class="visually-hidden" aria-live="off" data-paused="false" data-interacted="false" data-visual-assets="loading"></output>
  </main>
`;

function requiredElement<T extends HTMLElement>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Required element not found: ${selector}`);
  return element;
}

const gameShell = requiredElement<HTMLElement>(".game-shell");
const canvas = requiredElement<HTMLCanvasElement>("#game-canvas");
const welcomePanel = requiredElement<HTMLElement>("#welcome-panel");
const dialoguePanel = requiredElement<HTMLElement>("#dialogue-panel");
const portalPanel = requiredElement<HTMLElement>("#portal-panel");
const pausePanel = requiredElement<HTMLElement>("#pause-panel");
const challengePanel = requiredElement<HTMLElement>("#challenge-panel");
const mapPanel = requiredElement<HTMLElement>("#map-panel");
const journalPanel = requiredElement<HTMLElement>("#journal-panel");
const rewardPanel = requiredElement<HTMLElement>("#reward-panel");
const interactionPrompt = requiredElement<HTMLButtonElement>(
  "#interaction-prompt",
);
const touchControlsRoot = requiredElement<HTMLElement>("#touch-controls");
const interactionLabel = requiredElement<HTMLElement>("#interaction-label");
const announcement = requiredElement<HTMLOutputElement>("#announcement");
const gameState = requiredElement<HTMLOutputElement>("#game-state");
const questTitle = requiredElement<HTMLElement>("#quest-title");
const questStep = requiredElement<HTMLElement>("#quest-step");
const trailProgress = requiredElement<HTMLElement>("#trail-progress");
const fpsReading = requiredElement<HTMLElement>("#fps-reading");
const motionButton = requiredElement<HTMLButtonElement>("#motion-button");
const textButton = requiredElement<HTMLButtonElement>("#text-button");
const voiceButton = requiredElement<HTMLButtonElement>("#voice-button");
const welcomeExplorerOptions = requiredElement<HTMLElement>(
  "#welcome-explorer-options",
);
const welcomeExplorerStatus = requiredElement<HTMLOutputElement>(
  "#welcome-explorer-status",
);
const viewportMeta = requiredElement<HTMLMetaElement>("#viewport-meta");
const baseViewport =
  "width=device-width, initial-scale=1.0, viewport-fit=cover";

const touchCapable =
  navigator.maxTouchPoints > 0 ||
  window.matchMedia("(pointer: coarse)").matches;
document.documentElement.classList.toggle("touch-capable", touchCapable);
touchControlsRoot.hidden = !touchCapable;
gameState.dataset.visualQuality = touchCapable ? "balanced" : "high";
gameState.dataset.explorerScale = String(EXPLORER_VISUAL_SCALE);

const loaded = loadSave(window.localStorage);
let save: SaveGame = loaded.save;
let lastPositionWrite = 0;
let announcementTimer = 0;
let dialogueAfterClose: (() => void) | undefined;
const systemReducedMotion = window.matchMedia(
  "(prefers-reduced-motion: reduce)",
).matches;

function safariComplete(): boolean {
  return save.progress.completedQuestIds.includes(safariQuest.id);
}

function muscleComplete(): boolean {
  return save.progress.completedQuestIds.includes(muscleQuest.id);
}

function coastComplete(): boolean {
  return save.progress.completedQuestIds.includes(coastQuest.id);
}

function healthComplete(): boolean {
  return save.progress.completedQuestIds.includes(healthQuest.id);
}

function finaleComplete(): boolean {
  return save.progress.completedQuestIds.includes(finaleQuest.id);
}

function allRegionsComplete(): boolean {
  return (
    safariComplete() && muscleComplete() && coastComplete() && healthComplete()
  );
}

function reducedMotion(): boolean {
  return systemReducedMotion || save.settings.reducedMotion;
}

function persist(): void {
  save.updatedAt = new Date().toISOString();
  storeSave(window.localStorage, save);
}

function explorerOptionsMarkup(compact = false): string {
  return explorers
    .map(
      (
        explorer,
      ) => `<button type="button" class="explorer-choice ${compact ? "explorer-choice--compact" : ""}" data-explorer-choice="${explorer.id}" aria-pressed="${save.settings.explorerId === explorer.id}" aria-label="${explorer.name}, ${explorer.title}. ${explorer.description}">
        <span class="explorer-portrait ${explorer.portraitClass}" aria-hidden="true"><span class="explorer-portrait__hair"></span><span class="explorer-portrait__face"></span><span class="explorer-portrait__jacket"></span><span class="explorer-portrait__pack"></span></span>
        <span><strong>${explorer.name}</strong><small>${explorer.title}</small></span>
      </button>`,
    )
    .join("");
}

function resetInterfaceZoom(): void {
  viewportMeta.content = `${baseViewport}, minimum-scale=1, maximum-scale=1, user-scalable=no`;
  window.scrollTo(0, 0);
  window.requestAnimationFrame(() => {
    window.setTimeout(() => {
      viewportMeta.content = baseViewport;
    }, 250);
  });
  announce(
    "Interface view reset. Pinch zoom remains available in learning panels.",
  );
}

function isLearningPanelGesture(event: Event): boolean {
  return (
    event.target instanceof Element &&
    Boolean(event.target.closest(".modal-card"))
  );
}

function preventExplorationZoom(event: Event): void {
  if (!isLearningPanelGesture(event)) event.preventDefault();
}

for (const eventName of ["gesturestart", "gesturechange", "gestureend"]) {
  gameShell.addEventListener(eventName, preventExplorationZoom, {
    passive: false,
  });
}
gameShell.addEventListener(
  "touchmove",
  (event) => {
    if (event.touches.length > 1 && !isLearningPanelGesture(event))
      event.preventDefault();
  },
  { passive: false },
);

interface PendingModalTap {
  button: HTMLButtonElement;
  x: number;
  y: number;
  startedAt: number;
}

const pendingModalTaps = new Map<number, PendingModalTap>();
gameShell.addEventListener(
  "touchstart",
  (event) => {
    if (event.touches.length !== 1) {
      pendingModalTaps.clear();
      return;
    }
    const touch = event.changedTouches[0];
    const button =
      event.target instanceof Element
        ? event.target.closest<HTMLButtonElement>(
            ".modal-card button:not(:disabled)",
          )
        : null;
    if (!touch || !button) return;
    pendingModalTaps.set(touch.identifier, {
      button,
      x: touch.clientX,
      y: touch.clientY,
      startedAt: performance.now(),
    });
  },
  { passive: true },
);
gameShell.addEventListener(
  "touchend",
  (event) => {
    const touch = event.changedTouches[0];
    if (!touch) return;
    const pending = pendingModalTaps.get(touch.identifier);
    pendingModalTaps.delete(touch.identifier);
    if (!pending || !pending.button.isConnected || pending.button.disabled)
      return;
    const moved = Math.hypot(
      touch.clientX - pending.x,
      touch.clientY - pending.y,
    );
    if (moved > 14 || performance.now() - pending.startedAt > 800) return;
    event.preventDefault();
    pending.button.click();
  },
  { passive: false },
);
gameShell.addEventListener(
  "touchcancel",
  (event) => {
    for (const touch of event.changedTouches)
      pendingModalTaps.delete(touch.identifier);
  },
  { passive: true },
);

function announce(message: string): void {
  window.clearTimeout(announcementTimer);
  announcement.textContent = message;
  announcement.classList.add("announcement--visible");
  announcementTimer = window.setTimeout(() => {
    announcement.classList.remove("announcement--visible");
  }, 3_500);
}

function speak(text: string): void {
  if (!save.settings.readAloud || !("speechSynthesis" in window)) return;
  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = 0.88;
  utterance.pitch = 1.04;
  window.speechSynthesis.speak(utterance);
}

function updateSettingsLabels(): void {
  motionButton.textContent = `Reduced motion: ${save.settings.reducedMotion ? "on" : "off"}`;
  textButton.textContent = `Large text: ${save.settings.textScale === "large" ? "on" : "off"}`;
  voiceButton.textContent = `Read aloud: ${save.settings.readAloud ? "on" : "off"}`;
  document.documentElement.classList.toggle(
    "large-text",
    save.settings.textScale === "large",
  );
}

function updateQuestUI(): void {
  const safariCount = completedCount(save.progress, safariChallenges);
  const muscleCount = completedCount(save.progress, muscleChallenges);
  const coastCount = completedCount(save.progress, coastChallenges);
  const healthCount = completedCount(save.progress, healthChallenges);
  const regionName = requiredElement<HTMLElement>("#region-name");
  regionName.textContent =
    save.region === "sanctuary"
      ? "Wildlife Sanctuary"
      : save.region === "skeleton-safari"
        ? "Skeleton Safari"
        : save.region === "muscle-meadow"
          ? "Muscle Meadow"
          : save.region === "classification-coast"
            ? "Classification Coast"
            : "Healing Habitat";
  gameState.dataset.region = save.region;
  gameState.dataset.challengeCount = String(
    save.region === "muscle-meadow"
      ? muscleCount
      : save.region === "classification-coast"
        ? coastCount
        : save.region === "healing-habitat"
          ? healthCount
          : safariCount,
  );

  let visibleChallenges = safariChallenges;
  if (save.region === "healing-habitat") {
    visibleChallenges = healthChallenges;
    questTitle.textContent = healthComplete()
      ? "Care Greenhouse restored"
      : healthQuest.title;
    questStep.textContent = healthComplete()
      ? "Explore freely, replay safe-care stations, or return to the sanctuary."
      : `${healthCount} of ${healthChallenges.length} Safe-Care Lanterns lit.`;
  } else if (save.region === "classification-coast") {
    visibleChallenges = coastChallenges;
    questTitle.textContent = coastComplete()
      ? "Habitat Beacons restored"
      : coastQuest.title;
    questStep.textContent = coastComplete()
      ? "Explore freely, replay observation posts, or return to the sanctuary."
      : `${coastCount} of ${coastChallenges.length} Habitat Beacons relit.`;
  } else if (save.region === "muscle-meadow") {
    visibleChallenges = muscleChallenges;
    questTitle.textContent = muscleComplete()
      ? "Motion Stream restored"
      : muscleQuest.title;
    questStep.textContent = muscleComplete()
      ? "Explore freely, replay stations, or return to the sanctuary."
      : `${muscleCount} of ${muscleChallenges.length} Motion Sparks restored.`;
  } else if (save.region === "skeleton-safari") {
    questTitle.textContent = safariComplete()
      ? "Rabbit Trail restored"
      : safariQuest.title;
    questStep.textContent = safariComplete()
      ? "Explore freely, replay stations, or return to the sanctuary."
      : `${safariCount} of ${safariChallenges.length} trail clues restored.`;
  } else if (finaleComplete()) {
    questTitle.textContent = "Grand Wildlife Rescue complete";
    questStep.textContent =
      "Celebrate, customise your chameleon, explore, or replay any rescue.";
    visibleChallenges = finaleChallenges;
  } else if (allRegionsComplete()) {
    const finaleCount = completedCount(save.progress, finaleChallenges);
    questTitle.textContent = finaleQuest.title;
    questStep.textContent =
      finaleCount === 0
        ? "Meet your chameleon or use the trail map to begin the finale."
        : `${finaleCount} of ${finaleChallenges.length} sanctuary rescues complete.`;
    visibleChallenges = finaleChallenges;
  } else if (save.progress.completedQuestIds.includes(foundationQuest.id)) {
    questTitle.textContent = "Choose any nature trail";
    questStep.textContent =
      "North: Skeleton Safari · east: Muscle Meadow · west: Classification Coast · south: Healing Habitat.";
    visibleChallenges = !safariComplete()
      ? safariChallenges
      : !muscleComplete()
        ? muscleChallenges
        : !coastComplete()
          ? coastChallenges
          : healthChallenges;
  } else {
    questTitle.textContent = foundationQuest.title;
    questStep.textContent =
      "Meet your guide, or choose any of the four open gateways.";
  }

  trailProgress.innerHTML = visibleChallenges
    .map(
      ({ id, title }) =>
        `<span class="trail-dot ${save.progress.completedChallengeIds.includes(id) ? "trail-dot--complete" : ""}" title="${title}">${save.progress.completedChallengeIds.includes(id) ? "✓" : ""}</span>`,
    )
    .join("");
}

const stationTravel: Partial<
  Record<GameRegion, Array<{ label: string; position: PlayerPosition }>>
> = {
  sanctuary: [
    {
      label: "Chameleon meeting stone",
      position: { x: 3.2, y: 0.05, z: -1.8 },
    },
  ],
  "skeleton-safari": [
    { label: "Fossil Puzzle", position: { x: -8, y: 0.05, z: -58 } },
    { label: "Guardian Grove", position: { x: 7.5, y: 0.05, z: -59 } },
    { label: "Support Bridge", position: { x: -7.5, y: 0.05, z: -67 } },
    { label: "Model Glade", position: { x: 7.5, y: 0.05, z: -68 } },
    { label: "Bone & Body Match", position: { x: 0, y: 0.05, z: -72 } },
  ],
  "muscle-meadow": [
    { label: "Pair Power Workshop", position: { x: 58, y: 0.05, z: -7 } },
    { label: "Book Lift Lookout", position: { x: 65, y: 0.05, z: -2 } },
    { label: "Elastic Arm Model", position: { x: 65, y: 0.05, z: 8 } },
    { label: "Healthy Movement Trail", position: { x: 53, y: 0.05, z: 8 } },
  ],
  "classification-coast": [
    { label: "Backbone Beach", position: { x: -58, y: 0.05, z: -8 } },
    { label: "Armour Pools", position: { x: -66, y: 0.05, z: -3 } },
    { label: "Five-Group Lookout", position: { x: -65, y: 0.05, z: 8 } },
    {
      label: "Lighthouse Identification Key",
      position: { x: -55, y: 0.05, z: 10 },
    },
    { label: "Storm Key Repair", position: { x: -47, y: 0.05, z: 7 } },
  ],
  "healing-habitat": [
    { label: "Trusted Care Desk", position: { x: -8, y: 0.05, z: 58 } },
    { label: "Medicine Forms Gallery", position: { x: 7.5, y: 0.05, z: 59 } },
    { label: "Gentle Germ Trail", position: { x: -7.5, y: 0.05, z: 67 } },
    { label: "Prevention Garden", position: { x: 7.5, y: 0.05, z: 68 } },
    {
      label: "Trustworthy Research Greenhouse",
      position: { x: 0, y: 0.05, z: 72 },
    },
  ],
};

function renderTrailMap(): void {
  const content = requiredElement<HTMLElement>("#map-content");
  const regions: Array<{
    id: GameRegion;
    label: string;
    note: string;
  }> = [
    {
      id: "sanctuary",
      label: "Wildlife Sanctuary",
      note: "Central gardens and your chameleon guide",
    },
    {
      id: "skeleton-safari",
      label: "Skeleton Safari",
      note: "Fern's Rabbit Trail",
    },
    {
      id: "muscle-meadow",
      label: "Muscle Meadow",
      note: "Mara's Motion Stream",
    },
    {
      id: "classification-coast",
      label: "Classification Coast",
      note: "Cove's Habitat Beacons",
    },
    {
      id: "healing-habitat",
      label: "Healing Habitat",
      note: "Sage's Care Greenhouse",
    },
  ];
  const stations = stationTravel[save.region] ?? [];
  content.innerHTML = `
    <p class="map-intro">Use the trail map whenever walking feels awkward. All four science regions are open from the start, and travel never changes answers or evidence.</p>
    <div class="map-regions">${regions
      .map(
        ({ id, label, note }) =>
          `<button type="button" data-travel-region="${id}"><strong>${label}</strong><span>${note}</span></button>`,
      )
      .join("")}</div>
    ${stations.length ? `<section class="map-stations"><h3>${regions.find(({ id }) => id === save.region)?.label} destinations</h3><p>Travel beside a discovered learning station, then ${touchCapable ? "tap its named action" : "press <kbd>E</kbd> or select its named action"}.</p>${stations.map(({ label }, index) => `<button type="button" data-travel-station="${index}">${label}</button>`).join("")}</section>` : ""}
    ${allRegionsComplete() && !finaleComplete() ? `<button class="primary-button" id="map-finale" type="button">Continue the Grand Wildlife Rescue</button>` : ""}
  `;
  for (const button of content.querySelectorAll<HTMLButtonElement>(
    "[data-travel-region]",
  )) {
    button.addEventListener("click", () => {
      const region = button.dataset.travelRegion as GameRegion;
      mapPanel.hidden = true;
      enterRegion(region);
    });
  }
  for (const button of content.querySelectorAll<HTMLButtonElement>(
    "[data-travel-station]",
  )) {
    button.addEventListener("click", () => {
      const destination = stations[Number(button.dataset.travelStation)];
      if (!destination) return;
      save.player.position = { ...destination.position };
      persist();
      game.restart(save.player.position);
      mapPanel.hidden = true;
      game.setPaused(false);
      announce(
        `${destination.label} is just ahead. ${touchCapable ? "Tap the named action" : "Press E or select the named action"} when its prompt appears.`,
      );
    });
  }
  content.querySelector("#map-finale")?.addEventListener("click", () => {
    mapPanel.hidden = true;
    openNextFinaleRescue();
  });
}

function showDialogue(
  speaker: string,
  text: string,
  buttonLabel: string,
  afterClose?: () => void,
): void {
  requiredElement<HTMLElement>("#speaker").textContent = speaker;
  requiredElement<HTMLElement>("#speaker-portrait").textContent =
    speaker.includes("Fern")
      ? "🐇"
      : speaker.includes("Mara")
        ? "🦫"
        : speaker.includes("Cove")
          ? "🦀"
          : speaker.includes("Sage")
            ? "🐼"
            : "🦎";
  requiredElement<HTMLElement>("#dialogue-text").textContent = text;
  requiredElement<HTMLButtonElement>("#dialogue-close").textContent =
    buttonLabel;
  dialogueAfterClose = afterClose;
  dialoguePanel.hidden = false;
  game.setPaused(true);
  speak(text);
  requiredElement<HTMLButtonElement>("#dialogue-close").focus();
}

function openNextFinaleRescue(): void {
  const next = finaleChallenges.find(
    ({ id }) => !save.progress.completedChallengeIds.includes(id),
  );
  if (next) {
    finaleChallengeController.open(next);
    game.setPaused(true);
  }
}

function completeGuideQuest(): void {
  if (allRegionsComplete()) {
    const beat = finaleQuest.dialogue[finaleComplete() ? 1 : 0];
    showDialogue(
      beat?.speaker ?? "Chameleon guide",
      beat?.text ?? "The sanctuary is ready.",
      finaleComplete() ? "Keep exploring" : "Begin the next rescue",
      finaleComplete() ? undefined : openNextFinaleRescue,
    );
    return;
  }
  if (!save.progress.completedQuestIds.includes(foundationQuest.id)) {
    save.progress.completedQuestIds.push(foundationQuest.id);
    for (const rewardId of foundationQuest.rewardIds) {
      if (!save.progress.unlockedRewardIds.includes(rewardId)) {
        save.progress.unlockedRewardIds.push(rewardId);
      }
    }
    save.progress.objectiveEvidence["foundation.explore"] = {
      strongestEvidence: "independent",
      attempts: 1,
      lastAttemptAt: new Date().toISOString(),
    };
    persist();
  }
  gameState.dataset.interacted = "true";
  updateQuestUI();
  const beat = foundationQuest.dialogue[0];
  showDialogue(
    beat?.speaker ?? "Chameleon guide",
    beat?.text ?? "Welcome, explorer!",
    "Choose a science trail",
  );
}

function enterRegion(region: GameRegion): void {
  save.region = region;
  save.player.position =
    region === "sanctuary"
      ? { x: 0, y: 0.05, z: -9.5 }
      : region === "skeleton-safari"
        ? { x: 0, y: 0.05, z: -49 }
        : region === "muscle-meadow"
          ? { x: 49, y: 0.05, z: 0 }
          : region === "classification-coast"
            ? { x: -49, y: 0.05, z: 0 }
            : { x: 0, y: 0.05, z: 49 };
  persist();
  game.setRegion(region, save.player.position);
  updateQuestUI();
  announce(
    region === "sanctuary"
      ? "Back at the Wildlife Sanctuary."
      : region === "skeleton-safari"
        ? "Skeleton Safari discovered!"
        : region === "muscle-meadow"
          ? "Muscle Meadow discovered!"
          : region === "classification-coast"
            ? "Classification Coast discovered!"
            : "Healing Habitat discovered!",
  );
  const challengeSet =
    region === "skeleton-safari"
      ? safariChallenges
      : region === "muscle-meadow"
        ? muscleChallenges
        : region === "classification-coast"
          ? coastChallenges
          : region === "healing-habitat"
            ? healthChallenges
            : [];
  if (
    challengeSet.length &&
    completedCount(save.progress, challengeSet) === 0
  ) {
    const quest =
      region === "skeleton-safari"
        ? safariQuest
        : region === "muscle-meadow"
          ? muscleQuest
          : region === "classification-coast"
            ? coastQuest
            : healthQuest;
    const beat = quest.dialogue[0];
    showDialogue(
      beat?.speaker ?? "Animal guide",
      beat?.text ?? "Will you help restore this region?",
      region === "skeleton-safari"
        ? "Help Fern"
        : region === "muscle-meadow"
          ? "Help Mara"
          : region === "classification-coast"
            ? "Help Cove"
            : "Help Sage",
    );
  }
}

function completeChallenge(
  challenge: ChallengeDefinition,
  hintsUsed: number,
): void {
  save.progress = recordChallengeCompletion(
    save.progress,
    challenge,
    hintsUsed,
  );
  const isMuscle = challenge.id.startsWith("muscle.");
  const isCoast = challenge.id.startsWith("coast.");
  const isHealth = challenge.id.startsWith("health.");
  const isFinale = challenge.id.startsWith("finale.");
  const challenges = isFinale
    ? finaleChallenges
    : isHealth
      ? healthChallenges
      : isCoast
        ? coastChallenges
        : isMuscle
          ? muscleChallenges
          : safariChallenges;
  const quest = isFinale
    ? finaleQuest
    : isHealth
      ? healthQuest
      : isCoast
        ? coastQuest
        : isMuscle
          ? muscleQuest
          : safariQuest;
  const count = completedCount(save.progress, challenges);
  const wasComplete = save.progress.completedQuestIds.includes(quest.id);
  if (count === challenges.length && !wasComplete) {
    save.progress.completedQuestIds.push(quest.id);
    for (const rewardId of quest.rewardIds) {
      if (!save.progress.unlockedRewardIds.includes(rewardId)) {
        save.progress.unlockedRewardIds.push(rewardId);
      }
    }
    game.setRestored(
      safariComplete(),
      muscleComplete(),
      coastComplete(),
      healthComplete(),
      finaleComplete(),
    );
    if (isFinale && save.region !== "sanctuary") enterRegion("sanctuary");
    const reward = isFinale
      ? {
          icon: "🦎",
          region: "Grand Wildlife Rescue complete",
          title: "Grand Wildlife Rescuer!",
          text: "Every sanctuary signal is shining. You earned the Grand Wildlife Rescuer Badge and a Starlight Scarf for your chameleon. All trails remain open.",
          speech:
            "Grand Wildlife Rescue complete. You earned the Grand Wildlife Rescuer Badge and a Starlight Scarf. All trails remain open.",
        }
      : isHealth
        ? {
            icon: "🐾",
            region: "Healing Habitat restored",
            title: "Habitat Helper!",
            text: "Sage's Care Greenhouse is glowing. You earned the Habitat Helper Badge and a Care Greenhouse for the sanctuary.",
            speech:
              "Healing Habitat restored. You earned the Habitat Helper Badge and a Care Greenhouse.",
          }
        : isCoast
          ? {
              icon: "🐢",
              region: "Classification Coast restored",
              title: "Coast Pathfinder!",
              text: "Cove's Habitat Beacons are shining. You earned the Coast Pathfinder Badge and a Tidepool Observatory for the sanctuary.",
              speech:
                "Classification Coast restored. You earned the Coast Pathfinder Badge and a Tidepool Observatory.",
            }
          : isMuscle
            ? {
                icon: "🦦",
                region: "Muscle Meadow restored",
                title: "Motion Ranger!",
                text: "Mara's stream is flowing again. You earned the Beaver Motion Badge and a Movement Garden for the sanctuary.",
                speech:
                  "Muscle Meadow restored. You earned the Beaver Motion Badge and a Movement Garden.",
              }
            : {
                icon: "🐇",
                region: "Skeleton Safari restored",
                title: "Rabbit Ranger!",
                text: "Fern's trail is shining again. You earned the Rabbit Ranger Badge and a Fossil Garden for the sanctuary.",
                speech:
                  "Skeleton Safari restored. You earned the Rabbit Ranger Badge and a Fossil Garden.",
              };
    window.setTimeout(() => {
      requiredElement<HTMLElement>("#reward-icon").textContent = reward.icon;
      requiredElement<HTMLElement>("#reward-region").textContent =
        reward.region;
      requiredElement<HTMLElement>("#reward-title").textContent = reward.title;
      requiredElement<HTMLElement>("#reward-text").textContent = reward.text;
      rewardPanel.hidden = false;
      game.setPaused(true);
      speak(reward.speech);
      requiredElement<HTMLButtonElement>("#reward-close").focus();
    }, 100);
  }
  persist();
  updateQuestUI();
  renderJournal();
  announce(
    `${challenge.title} restored! ${count} of ${challenges.length} ${isFinale ? "sanctuary rescues" : isHealth ? "Safe-Care Lanterns" : isCoast ? "Habitat Beacons" : isMuscle ? "Motion Sparks" : "trail clues"} found.`,
  );
}

const portalDestinations = {
  "safari-portal": {
    label: "Skeleton Safari",
    region: "skeleton-safari",
  },
  "muscle-portal": { label: "Muscle Meadow", region: "muscle-meadow" },
  "coast-portal": {
    label: "Classification Coast",
    region: "classification-coast",
  },
  "health-portal": {
    label: "Healing Habitat",
    region: "healing-habitat",
  },
  "sanctuary-portal": { label: "Wildlife Sanctuary", region: "sanctuary" },
  "meadow-sanctuary-portal": {
    label: "Wildlife Sanctuary",
    region: "sanctuary",
  },
  "coast-sanctuary-portal": {
    label: "Wildlife Sanctuary",
    region: "sanctuary",
  },
  "health-sanctuary-portal": {
    label: "Wildlife Sanctuary",
    region: "sanctuary",
  },
} as const satisfies Record<string, { label: string; region: GameRegion }>;

type PortalId = keyof typeof portalDestinations;
let pendingPortalId: PortalId | undefined;
let declinedPortalId: PortalId | undefined;
let availableInteraction: WorldInteraction | undefined;

function isPortalId(id: string): id is PortalId {
  return id in portalDestinations;
}

function showPortalConfirmation(id: PortalId): void {
  if (!welcomePanel.hidden || !portalPanel.hidden || declinedPortalId === id)
    return;
  pendingPortalId = id;
  const destination = portalDestinations[id];
  requiredElement<HTMLElement>("#portal-title").textContent =
    `Go to ${destination.label}?`;
  requiredElement<HTMLElement>("#portal-description").textContent =
    `This gateway leads to ${destination.label}. Would you like to travel there now?`;
  requiredElement<HTMLButtonElement>("#portal-go").textContent =
    `Go to ${destination.label}`;
  interactionPrompt.hidden = true;
  portalPanel.hidden = false;
  game.setPaused(true);
  requiredElement<HTMLButtonElement>("#portal-go").focus();
}

function requestInteraction(id: string): void {
  if (isPortalId(id)) showPortalConfirmation(id);
  else handleInteraction(id);
}

function handleInteraction(id: string): void {
  if (id === "chameleon-guide") completeGuideQuest();
  else if (isPortalId(id)) enterRegion(portalDestinations[id].region);
  else if (id === "safari-rabbit") {
    const beat = safariQuest.dialogue[safariComplete() ? 1 : 0];
    showDialogue(
      beat?.speaker ?? "Fern the rabbit",
      beat?.text ?? "Welcome to the Rabbit Trail.",
      safariComplete() ? "Explore the restored trail" : "Restore the clues",
    );
  } else if (id === "meadow-beaver") {
    const beat = muscleQuest.dialogue[muscleComplete() ? 1 : 0];
    showDialogue(
      beat?.speaker ?? "Mara the beaver",
      beat?.text ?? "Welcome to the Motion Stream.",
      muscleComplete() ? "Explore the flowing meadow" : "Restore the stream",
    );
  } else if (id === "coast-crab") {
    const beat = coastQuest.dialogue[coastComplete() ? 1 : 0];
    showDialogue(
      beat?.speaker ?? "Cove the crab",
      beat?.text ?? "Welcome to Classification Coast.",
      coastComplete() ? "Explore the shining coast" : "Relight the beacons",
    );
  } else if (id === "health-panda") {
    const beat = healthQuest.dialogue[healthComplete() ? 1 : 0];
    showDialogue(
      beat?.speaker ?? "Sage the panda",
      beat?.text ?? "Welcome to Healing Habitat.",
      healthComplete()
        ? "Explore the glowing greenhouse"
        : "Light the lanterns",
    );
  } else {
    const healthChallenge = healthChallenges.find(
      ({ stationId }) => stationId === id,
    );
    const coastChallenge = coastChallenges.find(
      ({ stationId }) => stationId === id,
    );
    const muscleChallenge = muscleChallenges.find(
      ({ stationId }) => stationId === id,
    );
    const safariChallenge = safariChallenges.find(
      ({ stationId }) => stationId === id,
    );
    if (healthChallenge) healthChallengeController.open(healthChallenge);
    else if (coastChallenge) coastChallengeController.open(coastChallenge);
    else if (muscleChallenge) muscleChallengeController.open(muscleChallenge);
    else if (safariChallenge) challengeController.open(safariChallenge);
    if (healthChallenge || coastChallenge || muscleChallenge || safariChallenge)
      game.setPaused(true);
  }
}

function renderJournal(): void {
  const content = requiredElement<HTMLElement>("#journal-content");
  const sections = [
    {
      icon: "🐇",
      title: "Skeleton Safari",
      complete: safariComplete(),
      earned: "Rabbit Ranger Badge earned",
      countLabel: `${completedCount(save.progress, safariChallenges)} of ${safariChallenges.length} trail clues`,
      objectives: safariObjectives,
      challenges: safariChallenges,
    },
    {
      icon: "🦦",
      title: "Muscle Meadow",
      complete: muscleComplete(),
      earned: "Beaver Motion Badge earned",
      countLabel: `${completedCount(save.progress, muscleChallenges)} of ${muscleChallenges.length} Motion Sparks`,
      objectives: muscleObjectives,
      challenges: muscleChallenges,
    },
    {
      icon: "🐢",
      title: "Classification Coast",
      complete: coastComplete(),
      earned: "Coast Pathfinder Badge earned",
      countLabel: `${completedCount(save.progress, coastChallenges)} of ${coastChallenges.length} Habitat Beacons`,
      objectives: coastObjectives,
      challenges: coastChallenges,
    },
    {
      icon: "🐾",
      title: "Healing Habitat",
      complete: healthComplete(),
      earned: "Habitat Helper Badge earned",
      countLabel: `${completedCount(save.progress, healthChallenges)} of ${healthChallenges.length} Safe-Care Lanterns`,
      objectives: healthObjectives,
      challenges: healthChallenges,
    },
    {
      icon: "🦎",
      title: "Grand Wildlife Rescue",
      complete: finaleComplete(),
      earned: "Grand Wildlife Rescuer Badge earned",
      countLabel: `${completedCount(save.progress, finaleChallenges)} of ${finaleChallenges.length} sanctuary rescues`,
      objectives: finaleObjectives,
      challenges: finaleChallenges,
    },
  ];
  content.innerHTML = sections
    .map(
      (section) => `
        <section class="journal-region" aria-label="${section.title}">
          <div class="journal-summary">
            <span class="journal-badge ${section.complete ? "journal-badge--earned" : ""}" aria-hidden="true">${section.icon}</span>
            <div><strong>${section.complete ? section.earned : section.countLabel}</strong><p>${section.title} · evidence stays private in this browser.</p></div>
          </div>
          <ul class="objective-list">
            ${section.objectives
              .map((objective) => {
                const progress = save.progress.objectiveEvidence[objective.id];
                const label = progress
                  ? progress.strongestEvidence === "independent"
                    ? "I showed this"
                    : "Completed with a hint"
                  : "Still to discover";
                return `<li><span class="evidence-mark evidence-mark--${progress?.strongestEvidence ?? "new"}">${progress ? (progress.strongestEvidence === "independent" ? "★" : "✓") : "·"}</span><div><strong>${objective.title}</strong><span>${label}</span></div></li>`;
              })
              .join("")}
          </ul>
          <div class="journal-replays"><h3>Replay a field station</h3>
            ${section.challenges
              .map((challenge) => {
                const discovered = save.progress.completedChallengeIds.includes(
                  challenge.id,
                );
                return `<button type="button" class="secondary-button secondary-button--compact" data-replay="${challenge.id}" ${discovered ? "" : "disabled"}>${discovered ? challenge.title : `Undiscovered: ${challenge.title}`}</button>`;
              })
              .join("")}
          </div>
        </section>`,
    )
    .join("");
  content.insertAdjacentHTML(
    "afterbegin",
    `<section class="explorer-settings" aria-labelledby="journal-explorer-title"><h3 id="journal-explorer-title">Your explorer</h3><p>Change explorer without changing any trail progress or rewards.</p><div class="explorer-grid explorer-grid--compact">${explorerOptionsMarkup(true)}</div></section>`,
  );
  content.insertAdjacentHTML(
    "beforeend",
    `<section class="journal-tools" aria-label="Field journal reference and rewards">
      <details><summary>Living Things vocabulary</summary><dl class="vocabulary-list">${chapterVocabulary.map(([word, definition]) => `<div><dt>${word}</dt><dd>${definition}</dd></div>`).join("")}</dl></details>
      <div class="badge-shelf"><h3>Creature badges</h3><p>${[
        ["badge.rabbit-ranger", "🐇 Rabbit Ranger"],
        ["badge.otter-motion", "🦫 Beaver Motion"],
        ["badge.coast-pathfinder", "🐢 Coast Pathfinder"],
        ["badge.habitat-helper", "🐾 Habitat Helper"],
        ["badge.grand-wildlife-rescuer", "🦎 Grand Wildlife Rescuer"],
      ]
        .map(
          ([id, label]) =>
            `<span class="badge-chip ${save.progress.unlockedRewardIds.includes(id ?? "") ? "badge-chip--earned" : ""}">${save.progress.unlockedRewardIds.includes(id ?? "") ? label : "Undiscovered badge"}</span>`,
        )
        .join("")}</p></div>
      <div class="companion-styles"><h3>Chameleon trail scarf</h3><p>Choose an unlocked style. This preference stays only in this browser.</p>${[
        ["leaf", "Leaf green", true],
        ["sunset", "Sunset coral", muscleComplete()],
        ["ocean", "Ocean blue", coastComplete()],
        ["starlight", "Starlight gold", finaleComplete()],
      ]
        .map(
          ([style, label, unlocked]) =>
            `<button type="button" data-companion-style="${style}" ${unlocked ? "" : "disabled"} aria-pressed="${save.settings.companionStyle === style}">${unlocked ? label : `${label}—locked`}</button>`,
        )
        .join("")}</div>
    </section>`,
  );

  wireExplorerChoices(content);
  for (const button of content.querySelectorAll<HTMLButtonElement>(
    "[data-replay]",
  )) {
    button.addEventListener("click", () => {
      const finaleChallenge = finaleChallenges.find(
        ({ id }) => id === button.dataset.replay,
      );
      const healthChallenge = healthChallenges.find(
        ({ id }) => id === button.dataset.replay,
      );
      const coastChallenge = coastChallenges.find(
        ({ id }) => id === button.dataset.replay,
      );
      const muscleChallenge = muscleChallenges.find(
        ({ id }) => id === button.dataset.replay,
      );
      const safariChallenge = safariChallenges.find(
        ({ id }) => id === button.dataset.replay,
      );
      journalPanel.hidden = true;
      if (finaleChallenge) finaleChallengeController.open(finaleChallenge);
      else if (healthChallenge) healthChallengeController.open(healthChallenge);
      else if (coastChallenge) coastChallengeController.open(coastChallenge);
      else if (muscleChallenge) muscleChallengeController.open(muscleChallenge);
      else if (safariChallenge) challengeController.open(safariChallenge);
    });
  }
  for (const button of content.querySelectorAll<HTMLButtonElement>(
    "[data-companion-style]",
  )) {
    button.addEventListener("click", () => {
      const style = button.dataset.companionStyle;
      if (
        style === "leaf" ||
        style === "sunset" ||
        style === "ocean" ||
        style === "starlight"
      ) {
        save.settings.companionStyle = style;
        game.setCompanionStyle(style);
        persist();
        renderJournal();
      }
    });
  }
}

let touchControls: TouchControls | undefined;

const game = new SanctuaryGame(
  canvas,
  {
    onInteractionAvailable(interaction?: WorldInteraction) {
      availableInteraction = interaction;
      interactionPrompt.hidden = !interaction;
      if (!interaction) declinedPortalId = undefined;
      if (interaction) {
        interactionLabel.textContent = interaction.label;
        interactionPrompt.setAttribute(
          "aria-label",
          `${interaction.label}. Activate nearby action`,
        );
        if (isPortalId(interaction.id)) {
          const portalId = interaction.id;
          window.queueMicrotask(() => showPortalConfirmation(portalId));
        }
      }
    },
    onInteraction(id) {
      requestInteraction(id);
    },
    onPauseChange(paused) {
      gameState.dataset.paused = String(paused);
      touchControlsRoot.classList.toggle("touch-controls--inactive", paused);
      touchControls?.clear();
      if (
        !dialoguePanel.hidden ||
        !portalPanel.hidden ||
        !challengePanel.hidden ||
        !mapPanel.hidden ||
        !journalPanel.hidden ||
        !rewardPanel.hidden ||
        !welcomePanel.hidden
      ) {
        return;
      }
      pausePanel.hidden = !paused;
      if (paused) requiredElement<HTMLButtonElement>("#resume-button").focus();
      else game.focus();
    },
    onCameraChange(yaw) {
      gameState.dataset.cameraYaw = String(yaw);
    },
    onPlayerFacingChange(yaw) {
      gameState.dataset.facingYaw = String(yaw);
    },
    onPositionChange(position: PlayerPosition) {
      gameState.dataset.x = String(position.x);
      gameState.dataset.y = String(position.y);
      gameState.dataset.z = String(position.z);
      save.player.position = position;
      const now = performance.now();
      if (now - lastPositionWrite > 1_000) {
        lastPositionWrite = now;
        persist();
      }
    },
    onCompanionPositionChange(position, distanceFromPlayer, facingYaw, visual) {
      gameState.dataset.foxX = String(position.x);
      gameState.dataset.foxY = String(position.y);
      gameState.dataset.foxZ = String(position.z);
      gameState.dataset.foxDistance = String(distanceFromPlayer);
      gameState.dataset.foxFacingYaw = String(facingYaw);
      gameState.dataset.foxVisual = visual;
    },
    onCastVisualChange(summary) {
      gameState.dataset.cubePets = summary;
    },
    onPerformanceSample(framesPerSecond) {
      gameState.dataset.fps = String(framesPerSecond);
      fpsReading.textContent = `${framesPerSecond} frames per second`;
    },
    onVisualAssetsChange(status) {
      gameState.dataset.visualAssets = status;
    },
    reducedMotion,
  },
  save.player.position,
  save.region,
  save.settings.explorerId,
  touchCapable ? "balanced" : "high",
);
if (touchCapable) {
  touchControls = new TouchControls(touchControlsRoot, {
    onMove: (forward, right) => game.setTouchMovement(forward, right),
    onLook: (deltaX, deltaY) => game.rotateCamera(deltaX, deltaY),
    onJump: () => game.jump(),
  });
}
game.setRestored(
  safariComplete(),
  muscleComplete(),
  coastComplete(),
  healthComplete(),
  finaleComplete(),
);
game.setCompanionStyle(save.settings.companionStyle);
gameState.dataset.explorer = save.settings.explorerId;
game.setPaused(true);

interactionPrompt.addEventListener("click", () => game.interact());
requiredElement<HTMLButtonElement>("#portal-go").addEventListener(
  "click",
  () => {
    const id = pendingPortalId;
    if (!id) return;
    pendingPortalId = undefined;
    portalPanel.hidden = true;
    handleInteraction(id);
    game.setPaused(false);
  },
);
requiredElement<HTMLButtonElement>("#portal-not-now").addEventListener(
  "click",
  () => {
    declinedPortalId = pendingPortalId;
    pendingPortalId = undefined;
    portalPanel.hidden = true;
    interactionPrompt.hidden = true;
    game.setPaused(false);
    game.focus();
  },
);

function chooseExplorer(value: string, announceChange = true): void {
  if (!isExplorerId(value)) return;
  save.settings.explorerId = value;
  game.setExplorer(value);
  gameState.dataset.explorer = value;
  persist();
  renderWelcomeExplorerPicker();
  if (!journalPanel.hidden) renderJournal();
  if (announceChange) {
    const explorer = explorers.find(({ id }) => id === value)!;
    announce(`${explorer.name}, ${explorer.title}, selected.`);
  }
}

function wireExplorerChoices(root: ParentNode): void {
  for (const button of root.querySelectorAll<HTMLButtonElement>(
    "[data-explorer-choice]",
  )) {
    button.addEventListener("click", () => {
      chooseExplorer(button.dataset.explorerChoice ?? "");
    });
  }
}

function renderWelcomeExplorerPicker(): void {
  welcomeExplorerOptions.innerHTML = explorerOptionsMarkup();
  const selected = explorers.find(({ id }) => id === save.settings.explorerId)!;
  welcomeExplorerStatus.textContent = `${selected.name} is ready to explore.`;
  wireExplorerChoices(welcomeExplorerOptions);
}

renderWelcomeExplorerPicker();

const challengeController = new SkeletonChallengeController({
  panel: challengePanel,
  onComplete: completeChallenge,
  onClose() {
    if (rewardPanel.hidden) game.setPaused(false);
  },
  speak,
});

const muscleChallengeController = new MuscleChallengeController({
  panel: challengePanel,
  onComplete: completeChallenge,
  onClose() {
    if (rewardPanel.hidden) game.setPaused(false);
  },
  speak,
});

const coastChallengeController = new CoastChallengeController({
  panel: challengePanel,
  onComplete: completeChallenge,
  onClose() {
    if (rewardPanel.hidden) game.setPaused(false);
  },
  speak,
});

const healthChallengeController = new HealthChallengeController({
  panel: challengePanel,
  onComplete: completeChallenge,
  onClose() {
    if (rewardPanel.hidden) game.setPaused(false);
  },
  speak,
});

const finaleChallengeController = new FinaleChallengeController({
  panel: challengePanel,
  onComplete: completeChallenge,
  onClose() {
    if (rewardPanel.hidden) game.setPaused(false);
  },
  speak,
});

document.addEventListener(
  "keydown",
  (event) => {
    const visibleModal = [
      welcomePanel,
      dialoguePanel,
      portalPanel,
      challengePanel,
      mapPanel,
      journalPanel,
      rewardPanel,
      pausePanel,
    ].find((panel) => !panel.hidden);
    if (!visibleModal) return;
    if (event.code === "Escape" && visibleModal !== welcomePanel) {
      event.preventDefault();
      event.stopImmediatePropagation();
      const close =
        visibleModal === dialoguePanel
          ? requiredElement<HTMLButtonElement>("#dialogue-close")
          : visibleModal === portalPanel
            ? requiredElement<HTMLButtonElement>("#portal-not-now")
            : visibleModal === mapPanel
              ? requiredElement<HTMLButtonElement>("#map-close")
              : visibleModal === journalPanel
                ? requiredElement<HTMLButtonElement>("#journal-close")
                : visibleModal === rewardPanel
                  ? requiredElement<HTMLButtonElement>("#reward-close")
                  : visibleModal === pausePanel
                    ? requiredElement<HTMLButtonElement>("#resume-button")
                    : visibleModal.querySelector<HTMLButtonElement>(
                        "[data-action='close']",
                      );
      close?.click();
      return;
    }
    if (event.code !== "Tab") return;
    const focusable = [
      ...visibleModal.querySelectorAll<HTMLElement>(
        "button:not(:disabled), input:not(:disabled), select:not(:disabled), summary, [tabindex]:not([tabindex='-1'])",
      ),
    ].filter((element) => element.getClientRects().length > 0);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last?.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first?.focus();
    }
  },
  { capture: true },
);

document.addEventListener(
  "keydown",
  (event) => {
    if (
      !welcomePanel.hidden &&
      event.code !== "Tab" &&
      event.code !== "Enter"
    ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  },
  { capture: true },
);

requiredElement<HTMLButtonElement>("#start-button").addEventListener(
  "click",
  () => {
    welcomePanel.hidden = true;
    game.setPaused(false);
    game.focus();
    if (availableInteraction && isPortalId(availableInteraction.id))
      showPortalConfirmation(availableInteraction.id);
    updateQuestUI();
    announce(
      save.region === "sanctuary"
        ? allRegionsComplete()
          ? finaleComplete()
            ? "The sanctuary celebration is glowing. Every trail remains open."
            : "All four regions are restored. Begin the Grand Wildlife Rescue."
          : "Choose any open gateway: north, east, west, or south."
        : save.region === "muscle-meadow"
          ? muscleComplete()
            ? "Explore the flowing Motion Stream or replay a station."
            : "Restore the four Motion Sparks."
          : save.region === "classification-coast"
            ? coastComplete()
              ? "Explore the shining coast or replay an observation post."
              : "Relight the five Habitat Beacons."
            : save.region === "healing-habitat"
              ? healthComplete()
                ? "Explore the glowing Care Greenhouse or replay a safe-care station."
                : "Light the five Safe-Care Lanterns."
              : safariComplete()
                ? "Explore the restored Rabbit Trail or replay a field station."
                : "Restore the five Rabbit Trail clues.",
    );
  },
);
requiredElement<HTMLButtonElement>("#zoom-reset-button").addEventListener(
  "click",
  resetInterfaceZoom,
);
requiredElement<HTMLButtonElement>("#pause-button").addEventListener(
  "click",
  () => game.togglePause(),
);
requiredElement<HTMLButtonElement>("#resume-button").addEventListener(
  "click",
  () => game.setPaused(false),
);
requiredElement<HTMLButtonElement>("#restart-button").addEventListener(
  "click",
  () => {
    game.restart();
    game.setPaused(false);
    announce("Back at this region's safe entrance.");
  },
);
requiredElement<HTMLButtonElement>("#dialogue-close").addEventListener(
  "click",
  () => {
    dialoguePanel.hidden = true;
    const callback = dialogueAfterClose;
    dialogueAfterClose = undefined;
    callback?.();
    if (challengePanel.hidden) game.setPaused(false);
  },
);
requiredElement<HTMLButtonElement>("#map-button").addEventListener(
  "click",
  () => {
    renderTrailMap();
    mapPanel.hidden = false;
    game.setPaused(true);
    requiredElement<HTMLButtonElement>("#map-close").focus();
  },
);
requiredElement<HTMLButtonElement>("#map-close").addEventListener(
  "click",
  () => {
    mapPanel.hidden = true;
    game.setPaused(false);
  },
);
requiredElement<HTMLButtonElement>("#journal-button").addEventListener(
  "click",
  () => {
    renderJournal();
    journalPanel.hidden = false;
    game.setPaused(true);
    requiredElement<HTMLButtonElement>("#journal-close").focus();
  },
);
requiredElement<HTMLButtonElement>("#journal-close").addEventListener(
  "click",
  () => {
    journalPanel.hidden = true;
    game.setPaused(false);
  },
);
requiredElement<HTMLButtonElement>("#reward-close").addEventListener(
  "click",
  () => {
    rewardPanel.hidden = true;
    game.setPaused(false);
  },
);
motionButton.addEventListener("click", () => {
  save.settings.reducedMotion = !save.settings.reducedMotion;
  persist();
  updateSettingsLabels();
});
textButton.addEventListener("click", () => {
  save.settings.textScale =
    save.settings.textScale === "normal" ? "large" : "normal";
  persist();
  updateSettingsLabels();
});
voiceButton.addEventListener("click", () => {
  save.settings.readAloud = !save.settings.readAloud;
  if (!save.settings.readAloud && "speechSynthesis" in window) {
    window.speechSynthesis.cancel();
  }
  persist();
  updateSettingsLabels();
  if (save.settings.readAloud) speak("Read aloud is on.");
});
requiredElement<HTMLButtonElement>("#export-button").addEventListener(
  "click",
  () => {
    const confirmed = window.confirm(
      "Adult check: download a local copy of progress and settings? It contains no name, account, position, or health information and is not sent anywhere.",
    );
    if (!confirmed) return;
    const blob = new Blob([serializeLocalProgressExport(save)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "lilys-adventure-local-progress.json";
    link.click();
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
  },
);
requiredElement<HTMLButtonElement>("#reset-button").addEventListener(
  "click",
  () => {
    const confirmed = window.confirm(
      "Adult check: erase all saved adventure progress and settings? This cannot be undone.",
    );
    if (!confirmed) return;
    save = resetSave(window.localStorage);
    window.location.reload();
  },
);
window.addEventListener("beforeunload", persist);

if (loaded.recovered) {
  announce(
    "The old save could not be read, so a safe new adventure was started.",
  );
} else if (loaded.migrated) {
  persist();
  announce("Your earlier sanctuary progress was safely updated.");
}

updateSettingsLabels();
updateQuestUI();
renderJournal();
requiredElement<HTMLButtonElement>("#start-button").focus();
requestAnimationFrame(() => {
  gameState.dataset.loadMs = String(Math.round(performance.now()));
});

if (import.meta.hot) {
  import.meta.hot.dispose(() => game.dispose());
}
